import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs/internal/Subject';

@Injectable({ providedIn: 'root' })
export class OpenAiService implements OnDestroy {
  private API_URL = '/api/open-ai/';
  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  private _destroyed$ = new Subject<any>();

  constructor(
    private httpClient: HttpClient
  ) { }

  ngOnDestroy() {
    this._destroyed$.next(null);
    this._destroyed$.complete();
  }


  triggerTestRun(): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + 'trigger-test');
  }


  generateOpenAiMessageFromPrompt(_prompt: string, _instructions?: string): Observable<any> {
    const aiChatObj = { prompt: _prompt, instructions: _instructions ?? null};
    return this.httpClient.post<any>(this.API_URL + 'generate-message-from-prompt', JSON.stringify(aiChatObj), this.headerOptions);
  }


  generateOpenAiMessageUsingFilePath(_prompt: string, _instructions?: string, _filePath?: string): Observable<any> {
    const aiChatObj = { prompt: _prompt, instructions: _instructions ?? null, filePath: _filePath ?? null };
    return this.httpClient.post<any>(this.API_URL + 'generate-message-using-filepath', JSON.stringify(aiChatObj), this.headerOptions);
  }


  // Proofread / grammar-check a block of text. Returns { corrected, edits } from the backend.
  proofread(_text: string, _mode: string = 'grammar'): Observable<any> {
    const proofreadObj = { text: _text ?? '', mode: _mode ?? 'grammar' };
    return this.httpClient.post<any>(this.API_URL + 'proofread', JSON.stringify(proofreadObj), this.headerOptions);
  }


  // Removes signature and empty content below actual message to pass to open ai dialog.
  cleanMessageContent(_message: string, _signature: string = ''): string {
    if (!_message?.length) return '';

    // Remove the signature if present
    let cleanedMessage = _signature?.length ? _message.replace(_signature, '') : _message;

    const isHtml = /<\/?[a-z][\s\S]*>/i.test(cleanedMessage); // basic HTML check

    if (isHtml) {
      // --- HTML MODE ---
      // Split into paragraph blocks
      const parts = cleanedMessage.split(/<\/p>/i).map(p => p.trim()).filter(p => p.length > 0);

      // Find last paragraph with real content
      const lastValidIndex = parts.map(p => p.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim())
        .reduceRight((acc, text, i) => acc === -1 && text.length > 0 ? i : acc, -1);

      if (lastValidIndex === -1) return ''; // All empty
      const keptParts = parts.slice(0, lastValidIndex + 1);
      cleanedMessage = keptParts.join('</p>') + '</p>';
    } else {
      // --- PLAIN TEXT MODE ---
      // Split into lines and find last non-empty line
      const lines = cleanedMessage.split(/\r?\n/);
      let lastValidIndex = -1;

      for (let i = lines.length - 1; i >= 0; i--) {
        if (lines[i].trim().length > 0) {
          lastValidIndex = i;
          break;
        }
      }

      cleanedMessage = lastValidIndex >= 0 ? lines.slice(0, lastValidIndex + 1).join('\n') : '';
    }

    return cleanedMessage.trim();
  }


  getFileTypeForModule(_moduleType: string): string {
    const resumeTypeModules = ['leads'];
    return resumeTypeModules.includes(_moduleType) ? 'resume' : 'file';
  }


  getPromptsForDialog(_messageType: string, _relatedRecordType: string, _relatedRecord: any, _attachedFilePath: string): string[] {
    const fileTypeToUse = this.getFileTypeForModule(_relatedRecordType);
    const hasFile = !!_attachedFilePath?.length;
    let promptsToUse: string[] = [];

    if (hasFile) {
      const fileDescription = (fileTypeToUse === 'resume') ? `candidate's resume` : 'attached file';
      promptsToUse = [
        `Write a personalized outreach ${_messageType} message tailored to the ${fileDescription}.`,
        `Highlight their strengths and make it engaging.`,
        `Add a greeting. Use the candidate's name if available in the resume or record.`,
      ];
    } else if (_relatedRecord?._id) {
      promptsToUse = [
        `Write a personalized outreach ${_messageType} message for this ${_relatedRecordType?.slice(0, -1) ?? 'candidate'}.`,
        `Use their available details to make it feel specific and genuine.`,
        `Be clear and concise, showing interest in their background or role.`,
      ];
    } else {
      promptsToUse = [
        `Write a general outreach message suitable for a recruiting ${_messageType}.`,
        `Make it friendly and professional.`,
        `Keep it concise and engaging.`,
      ];
    }

    return promptsToUse;
  }
}